// Multi-Language Recipe Type Definitions

export type SupportedLanguage = 'en' | 'es' | 'fr' | 'de';

export const SUPPORTED_LANGUAGES: SupportedLanguage[] = ['en', 'es', 'fr', 'de'];

export const LANGUAGE_NAMES: Record<SupportedLanguage, string> = {
  en: 'English',
  es: 'Español',
  fr: 'Français',
  de: 'Deutsch',
};

export const LANGUAGE_FLAGS: Record<SupportedLanguage, string> = {
  en: '🇺🇸',
  es: '🇪🇸',
  fr: '🇫🇷',
  de: '🇩🇪',
};

// Translation data for a single language
export interface RecipeTranslation {
  title: string;
  summary: string;
  // Ingredient names (localized, but quantities come from canonical)
  ingredientNames: string[];
  // Instruction text (localized, but order and structure from canonical)
  instructionTexts: string[];
  // Educational/helper text
  educationalText?: string;
  // "Before you start" section
  beforeYouStart?: string;
  // Safety notes (localized)
  safetyNotes?: string[];
}

// Audio metadata for a single language
export interface RecipeAudio {
  url: string;
  voiceId: string;
  durationSec: number;
  generatedAt: string;
}

// Canonical ingredient (quantity + unit)
export interface CanonicalIngredient {
  quantity: string;
  unit: string;
  // The ingredient name comes from translation
}

// Complete recipe metadata structure
export interface RecipeMetadata {
  // Canonical language (always 'en' for now)
  canonicalLanguage: 'en';
  
  // Translations for all supported languages
  translations: {
    [K in SupportedLanguage]: RecipeTranslation;
  };
  
  // Audio for all supported languages
  audio?: {
    [K in SupportedLanguage]?: RecipeAudio;
  };
  
  // Canonical data (consistent across all languages)
  canonical: {
    // Ingredient structure (quantities, units)
    ingredients: CanonicalIngredient[];
    // Number of instruction steps
    instructionCount: number;
    // Times
    prepTime: string;
    cookTime: string;
    totalTime: string;
    // Servings
    servings: string;
    // Difficulty
    difficulty: 'Easy' | 'Medium' | 'Hard';
    // Age range
    ageRange: string;
    // Allergens
    allergens: string[];
    // Diet tags
    dietTags: string[];
    // Nutrition (optional)
    nutrition?: {
      calories?: string;
      protein?: string;
      carbs?: string;
      fat?: string;
    };
  };
  
  // Image URLs (shared across languages)
  ingredientsImageUrl?: string;
  ingredientsImageAlt?: string;
  stepImages?: Array<{
    stepNumber: number;
    imageUrl: string;
    altText: string;
  }>;
}

// Localized recipe (canonical data + specific language translation)
export interface LocalizedRecipe {
  language: SupportedLanguage;
  title: string;
  summary: string;
  
  // Ingredients (merged: canonical quantities + localized names)
  ingredients: Array<{
    quantity: string;
    unit: string;
    name: string;
    fullText: string; // e.g., "2 cups flour"
  }>;
  
  // Instructions (localized text)
  instructions: string[];
  
  // Educational text
  educationalText?: string;
  beforeYouStart?: string;
  safetyNotes?: string[];
  
  // Canonical data
  prepTime: string;
  cookTime: string;
  totalTime: string;
  servings: string;
  difficulty: string;
  ageRange: string;
  allergens: string[];
  dietTags: string[];
  nutrition?: {
    calories?: string;
    protein?: string;
    carbs?: string;
    fat?: string;
  };
  
  // Images
  ingredientsImageUrl?: string;
  ingredientsImageAlt?: string;
  stepImages?: Array<{
    stepNumber: number;
    imageUrl: string;
    altText: string;
  }>;
  
  // Audio
  audio?: RecipeAudio;
}

// Helper to check if a language is supported
export function isSupportedLanguage(lang: string): lang is SupportedLanguage {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
}

// Helper to get default language
export function getDefaultLanguage(): SupportedLanguage {
  return 'en';
}

// Helper to validate language or return default
export function validateLanguage(lang: string | null | undefined): SupportedLanguage {
  if (!lang || !isSupportedLanguage(lang)) {
    return getDefaultLanguage();
  }
  return lang;
}

